/**
 * №13.1 (§1) — one editable value cell for the four-plane IS plane.
 *
 * Ports the older `FieldEditor` interaction (click-to-edit text/number/date/
 * email; Enter/blur commits, Esc cancels; enum = live <select>; richtext =
 * textarea; reference = chip/picker editor) into `RecordView`, and layers on the
 * two things §1 adds over the legacy field list:
 *   • a governed (locked) render — 🔒 + the value + the correction sentence —
 *     for fields frozen by the row's status (M4/N1); never an editable input.
 *   • a brief "Saved ✓" flash on a successful optimistic commit (M3).
 * The workflow/status field is passed `readOnly` so it never becomes a free-text
 * edit (N2) — status changes stay with the transition affordance.
 */
import { useState } from "react";
import type { FieldDefinition, ResolvedRef } from "../../../lib/types";
import { FieldEditor } from "../../components/entity/FieldEditor";
import { GovernedNotice } from "./GovernedNotice";

export function InlineFieldCell({
  field,
  value,
  refs,
  displayText,
  governedCorrection,
  readOnly,
  onCommit,
  onOpenRef,
}: {
  field: FieldDefinition;
  value: unknown;
  /** Resolved forward refs for this one field (reference/references editors). */
  refs?: ResolvedRef[];
  /** Precomputed display string for the read-only / governed branches. */
  displayText: string;
  /** When set, the field is frozen — render the lock affordance, not an input. */
  governedCorrection?: string;
  /** The workflow/status field: read-only here, changed via the transition UI. */
  readOnly?: boolean;
  /** Commit a new value; resolves on success, rejects (after rollback) on error. */
  onCommit: (value: unknown) => Promise<void>;
  onOpenRef?: (entityTypeSlug: string, entityId: string) => void;
}) {
  const [saved, setSaved] = useState(false);

  // M4/N1 — governed field: 🔒 + value (read-only) + the correction sentence.
  if (governedCorrection) {
    return (
      <div>
        <div className="v-readonly">{displayText}</div>
        <GovernedNotice message={governedCorrection} />
      </div>
    );
  }

  // N2 — the status field is never a free-text inline edit.
  if (readOnly) {
    return <div className="v-readonly">{displayText}</div>;
  }

  async function handleSave(v: unknown) {
    try {
      await onCommit(v);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1200);
    } catch {
      // Rollback + error toast are handled by the caller's commit; swallow here.
    }
  }

  return (
    <div style={{ position: "relative" }}>
      <FieldEditor field={field} value={value} refs={refs} onSave={handleSave} onOpenRef={onOpenRef} />
      {saved && (
        <span
          aria-live="polite"
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            fontSize: "var(--text-2xs)",
            fontWeight: "var(--weight-medium)",
            color: "var(--color-text-success)",
            pointerEvents: "none",
          }}
        >
          Saved ✓
        </span>
      )}
    </div>
  );
}
